import React, { useState } from "react";
import ToastPopup from "./ToastPopup";
import { AddUserPopupProps, createToastMessage, ToastMessage } from '../interFaces/interfaces';
import "../css/others/Popup.css";
import { validateField, nameValidationRules, emailValidationRules } from "../Reusablecomponents/Validation"
const AddUserPopup: React.FC<AddUserPopupProps> = ({ roles, selectedGroup, newUserData, handleInputChange, handleSaveNewUser, handleClosePopup, auditorSkills, auditorTypes }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<string[]>(newUserData.skills || []);
  const [fieldErrors, setFieldErrors] = useState<{ firstName?: string; lastName?: string; email?: string }>({});
  const isAuditorRole = roles.find(role => role.id === selectedGroup)?.name.toLowerCase() === 'auditor';
  const allSkillValues = auditorSkills.map(s => s.value);
  const isAllSelected = allSkillValues.length > 0 && allSkillValues.every(v => selectedSkills.includes(v));
  const handleAuditorTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isPrimary = e.target.value === 'primary';
    handleInputChange({ target: { name: 'isPrimaryAuditor', value: isPrimary } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };
  const handleAuditorTypeSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const auditorTypeValue = e.target.value;
    handleInputChange({ target: { name: 'isPrimaryAuditor', value: undefined } } as unknown as React.ChangeEvent<HTMLInputElement>);
    handleInputChange({ target: { name: 'auditorType', value: auditorTypeValue } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };
  const handleSkillChange = (skillValue: string) => {
    setSelectedSkills(prev => {
      const newSkills = prev.includes(skillValue) ? prev.filter(s => s !== skillValue) : [...prev, skillValue];
      handleInputChange({ target: { name: 'skills', value: newSkills } } as unknown as React.ChangeEvent<HTMLInputElement>);
      return newSkills;
    });
  };
  const handleInputChangeWithValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleInputChange(e);
    let error: string | null = null;
    if (name === 'firstName') {
      error = validateField(value, nameValidationRules, "First name");
    } else if (name === 'lastName') {
      error = validateField(value, nameValidationRules, "Last name");
    } else if (name === 'email') {
      error = validateField(value, emailValidationRules, "Email");
    }
    setFieldErrors(prev => ({
      ...prev,
      [name]: error || undefined
    }));
  };
  const handleValidationAndSubmit = () => {
    const { firstName, lastName, email, auditorType, isPrimaryAuditor, skills } = newUserData;
    const firstNameError = validateField(firstName, nameValidationRules, "First name");
    const lastNameError = validateField(lastName, nameValidationRules, "Last name");
    const emailError = validateField(email, emailValidationRules, "Email");
    const errors = { firstName: firstNameError || undefined, lastName: lastNameError || undefined, email: emailError || undefined };
    setFieldErrors(errors);
    if (firstNameError || lastNameError || emailError) {
      const firstError = firstNameError || lastNameError || emailError;
      setMessage(createToastMessage(firstError || "Please fix the validation errors", "error", 3000));
      return;
    }
    if (isAuditorRole && !auditorType) {
      setMessage(createToastMessage("Please select auditor type", "warning", 3000));
      return;
    }
    if (isAuditorRole && (auditorType === 'EXTERNAL' || auditorType === 'INTERNAL')) {
      if (isPrimaryAuditor === undefined) {
        setMessage(createToastMessage("Please select auditor committee", "warning", 3000));
        return;
      }
      if (!skills || skills.length === 0) {
        setMessage(createToastMessage("Please select at least one skill", "warning", 3000));
        return;
      }
    }
    handleSaveNewUser();
  };
  const handleSelectAllSkills = () => {
    let updatedSkills: string[] = [];
    if (isAllSelected) {
      updatedSkills = [];
    } else {
      updatedSkills = allSkillValues;
    }
    setSelectedSkills(updatedSkills);
    handleInputChange({ target: { name: 'skills', value: updatedSkills } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Add {roles.find((role) => role.id === selectedGroup)?.name}</h2>
        <form>
          <label>First Name:
            <input type="text" name="firstName" value={newUserData.firstName} onChange={handleInputChangeWithValidation} className={fieldErrors.firstName ? 'error' : ''} />
            {fieldErrors.firstName && <span className="error-message">{fieldErrors.firstName}</span>}
          </label>
          <label>Last Name:
            <input type="text" name="lastName" value={newUserData.lastName} onChange={handleInputChangeWithValidation} className={fieldErrors.lastName ? 'error' : ''} />
            {fieldErrors.lastName && <span className="error-message">{fieldErrors.lastName}</span>}
          </label>
          <label>Email:
            <input type="email" name="email" value={newUserData.email} onChange={handleInputChangeWithValidation} className={fieldErrors.email ? 'error' : ''} />
            {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
          </label>
          {isAuditorRole && (
            <label>Auditor Type:
              <select name="auditorType" value={newUserData.auditorType || ''} onChange={handleAuditorTypeSelectChange}>
                <option value="">Select Auditor Type</option>
                {auditorTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.description}</option>
                ))}
              </select>
            </label>
          )}
          {isAuditorRole && newUserData.auditorType && (
            <label>Auditor Committee:
              <select name="auditorType" onChange={handleAuditorTypeChange} value={newUserData.isPrimaryAuditor === true ? 'primary' : newUserData.isPrimaryAuditor === false ? 'secondary' : ''}>
                <option value="">Select Auditor Committee</option>
                <option value="primary">Primary Auditor</option>
                <option value="secondary">Secondary Auditor</option>
              </select>
            </label>
          )}
          {isAuditorRole && newUserData.auditorType && newUserData.isPrimaryAuditor !== undefined && (
            <label>Skills:
              <div className="skills-checkbox-container">
                <div className="skill-checkbox-item">
                  <input type="checkbox" id="select-all-skills" checked={isAllSelected} onChange={handleSelectAllSkills} />
                  <label htmlFor="select-all-skills"><strong>Select All Skills</strong></label>
                </div>
                {auditorSkills.map(skill => (
                  <div key={skill.value} className="skill-checkbox-item">
                    <input type="checkbox" id={`skill-${skill.value}`} checked={selectedSkills.includes(skill.value)} onChange={() => handleSkillChange(skill.value)} />
                    <label htmlFor={`skill-${skill.value}`}> {skill.description} </label>
                  </div>
                ))}
              </div>
            </label>
          )}
          <div className="modal-buttons">
            <button type="button" onClick={handleClosePopup}>Cancel</button>
            <button type="button" onClick={handleValidationAndSubmit}>Submit</button>
          </div>
        </form>
        {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      </div>
    </div>
  );
};
export default AddUserPopup;